import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider } from "react-hook-form";

import { Box } from "@UI/Components/Box/Box";
import { Button } from "@UI/Components/Button/Button";
import { Flex } from "@UI/Components/Flex/Flex";
import { Heading } from "@UI/Components/Heading/Heading";
import { Text } from "@UI/Components/Text/Text";
import { SimpleGrid } from "@UI/Components/SimpleGrid/SimpleGrid";
import { VStack } from "@UI/Components/Stack/Stack";
import { useToast } from "@UI/hooks/useToast";
import { useCurrentUser } from "@modules/Authentication/hooks/useCurrentUser";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import useI18n from "@I18n/hooks/useI18n";
import useForm from "@Libs/Utils/hooks/useForm";
import { useNavigate } from "@Libs/Router/hooks/useNavigate";
import { useRoutes } from "@Libs/Router/hooks/useRoutes";

import { serviceRequestPathFragment } from "../../../config/routes";
import { ContactInfoModal } from "../../Components/ContactInfoModal/ContactInfoModal";
import { PrefillBanner } from "../../Components/PrefillBanner/PrefillBanner";
import { TaskCreationLayout } from "../../Components/TaskCreationLayout/TaskCreationLayout";
import { TaskCreationOutcomeModals } from "../../Components/TaskCreationOutcomeModals/TaskCreationOutcomeModals";
import {
  getQuestionTitleMessage,
  getScreenSubtitleMessage,
  getScreenTitleMessage,
} from "../../utils/messageKeys";
import { AUTH_SCREEN_KEYS } from "../../config/constants";
import { getQuestionAdapter } from "../../config/questionRegistry";
import {
  buildTrackingParams,
  pushScreenEvent,
  TRACKING_EVENTS,
  trackFunnelEvent,
} from "../../config/tracking";
import { useTaskCreationFlow } from "../../Contexts/TaskCreationFlow";
import {
  dismissPrefill,
  getDismissedPrefills,
} from "../../hooks/useDraftStorage";
import { useAuthStepCompletion } from "../../hooks/useAuthStepCompletion";
import { useDraftGoneRedirect } from "../../hooks/useDraftGoneRedirect";
import { useDraftIdentity } from "../../hooks/useDraftIdentity";
import { useFlowBack } from "../../hooks/useFlowBack";
import { useFlowCategoryParams } from "../../hooks/useFlowPCategory";
import { useIsDraftSaving } from "../../hooks/useServiceRequestDraft";
import { useSubmitScreenAnswers } from "../../hooks/useSubmitScreenAnswers";
import { useSuggestedAddress } from "../../hooks/useSuggestedAddress";
import { useCurrentStepName } from "../../hooks/useTaskCreationSummary";
import messages from "../../messages";
import { IAnswerMap, IQuestion } from "../../types";
import { buildFlowShape } from "../../utils/buildFlowShape";
import { getInlineTrigger, isVisible } from "../../utils/conditions";
import { hasContactInfoError } from "../../utils/contactInfo";
import {
  isSuggestedAddress,
  suggestedAddressValues,
} from "../../utils/suggestedAddressValues";
import { toDraftFields } from "../../utils/toDraftFields";

/**
 * Any config screen, rendered from the config alone (RT-4321 M5).
 *
 * There is deliberately no per-category branch here: the screen's questions,
 * their types, their options and their required-ness all come out of the
 * category config, and each type is turned into props by its adapter in
 * `config/questionRegistry.tsx`. A type with no adapter renders nothing, so a
 * newer config degrades to a skipped question rather than a blank screen.
 *
 * A question whose `visible_if` names a single value on a sibling of the same
 * screen is rendered *inside* that option's row rather than as a block of its
 * own — `desired_date` in the "before this date" card. Which question that is
 * comes from `getInlineTrigger`, never from a screen key (settled decision 16).
 *
 * TODO(RT-4321): server field errors are not mapped onto fields (open decision
 * B: the PUT's per-question error shape is still owed by the backend), so a
 * rejected save only toasts.
 */
const CLAIM_FAILED_TOAST_ID = "task-creation-claim-failed";

export const DynamicScreen = () => {
  const { formatMessage } = useI18n();
  const navigate = useNavigate();
  const { generateRoute } = useRoutes();
  const toast = useToast();

  const [isPublishing, setIsPublishing] = useState(false);
  const [publishedRequestId, setPublishedRequestId] = useState<string | null>(
    null
  );

  const {
    currentScreenKey,
    screens,
    answers,
    draft,
    config,
    answeredQuestions,
    totalQuestions,
    currentStepNumber,
    totalSteps,
    isLoggedIn,
    isFlowLoading,
  } = useTaskCreationFlow();
  const stepName = useCurrentStepName();

  const { isEditing, mode, taskId, screenRoute } = useDraftIdentity();
  const { submit } = useSubmitScreenAnswers();
  // Cached from flow entry, so it predates this request's own publish.
  const { currentUser } = useCurrentUser();
  // Shared with the shell's back link, so both land on the same screen.
  const handleBack = useFlowBack();
  const categoryParams = useFlowCategoryParams();
  const isSaving = useIsDraftSaving();
  const handleDraftGone = useDraftGoneRedirect();
  const claim = useAuthStepCompletion(
    CLAIM_FAILED_TOAST_ID,
    currentScreenKey ?? ""
  );

  // Keyed by question, so one screen can eventually hold more than one async
  // question without them clobbering each other's busy state.
  const [busyQuestions, setBusyQuestions] = useState<Record<string, boolean>>(
    {}
  );
  const isAnyQuestionBusy = Object.values(busyQuestions).some(Boolean);

  const screenIndex = screens.findIndex(
    (screen) => screen.key === currentScreenKey
  );
  const screen = screens[screenIndex];
  const categoryKey = config?.category.key ?? "";
  // Only while there is nothing to show yet — a background refetch of an
  // already-rendered screen must not blank it back out.
  //
  // `isEditing` on its own as well: the edit flow never publishes and never
  // clears its token, so a missing screen there is always a shape still
  // settling — and the bare Back/Continue shell that used to show through must
  // not be visible at all.
  const showLoadingState = !screen && (isFlowLoading || isEditing);

  // This container doesn't remount between screens (only `currentScreenKey`
  // changes), so a screen left mid-upload could otherwise leave the next
  // screen's Continue permanently disabled by a question key it never had.
  useEffect(() => {
    setBusyQuestions({});
  }, [screen?.key]);

  // `screen.questions` is already filtered against the *saved* answers, which
  // drops an inline question until its trigger has been saved — and the trigger
  // lives on this very screen, so it never has been. The inline list therefore
  // comes off the config screen, and only the blocks come off the flow shape.
  const configQuestions = useMemo(
    () =>
      config?.steps
        .flatMap((step) => step.screens)
        .find((each) => each.key === screen?.key)?.questions ?? [],
    [config, screen?.key]
  );

  const inlineQuestions = useMemo(
    () =>
      configQuestions.flatMap((question) => {
        const trigger = getInlineTrigger(question, configQuestions);

        return trigger ? [{ question, trigger }] : [];
      }),
    [configQuestions]
  );

  const questions = (screen?.questions ?? []).filter(
    (question) =>
      !inlineQuestions.some((inline) => inline.question.key === question.key)
  );

  // The assistant's suggestions, minus the ones the user has already edited or
  // dismissed — those never come back (settled decision 8).
  const prefills = useMemo(() => {
    const dismissed = getDismissedPrefills();

    return (draft?.answers ?? []).reduce<IAnswerMap>((map, answer) => {
      // The suggested address is not the assistant's: no banner announces it.
      if (
        answer.ai_value != null &&
        !isSuggestedAddress(answer.ai_value) &&
        !dismissed.includes(answer.question_key)
      )
        map[answer.question_key] = answer.ai_value;

      return map;
    }, {});
  }, [draft?.answers]);

  // The banner is about the *screen*, not about how the visitor arrived: it
  // says "some of this is already filled in for you", so what it needs to know
  // is whether the assistant actually suggested anything for these questions.
  //
  // It used to be gated on an `s_category` in the URL as well, which meant it
  // vanished one hop into the flow — the param rides the entry URL only — even
  // though the prefills it was announcing were still sitting on the screen
  // (RT-4321).
  const hasPrefillForScreen = configQuestions.some(
    (question) => prefills[question.key] !== undefined
  );

  const defaultValues = useMemo(
    () =>
      configQuestions.reduce<IAnswerMap>((values, question) => {
        // `answers` already falls back to `ai_value` (`toAnswerMap`), so a
        // prefilled question lands preselected. `default` is the config's own
        // preselection (`audience` is "everyone") and is the weakest of the two.
        values[question.key] = answers[question.key] ?? question.default;

        return values;
      }, {}),
    [screen?.key]
  );

  // `values`, not `defaultValues`: the config can arrive a render *after* this
  // screen mounts, and `defaultValues` is read once, on the first render — which
  // left the config's own preselection (`audience` is "everyone") unselected in
  // the browser even though it was in the memo. `values` re-seeds when the memo
  // changes, and the memo is keyed on the screen, so it never clobbers typing.
  const methods = useForm<IAnswerMap>({ values: defaultValues });

  const suggestedAddress = useSuggestedAddress();

  // After the form's own re-seed, which runs first and would wipe it: the
  // suggestion can also resolve after the screen is up.
  useEffect(() => {
    Object.entries(
      suggestedAddressValues(
        configQuestions,
        methods.getValues(),
        suggestedAddress
      )
    ).forEach(([key, value]) => methods.setValue(key, value));
  }, [configQuestions, suggestedAddress]);

  // The inline questions' triggers sit on this same screen, so their visibility
  // has to follow the form's live values rather than the saved ones. What this
  // decides is whether the answer is *required* and whether it is *submitted* —
  // not whether the pill is drawn: per the design the date chip is visible on
  // its card whether or not the card is selected.
  const { errors } = methods.formState;
  const liveAnswers = { ...answers, ...methods.watch() };
  const isInlineActive = (question: IQuestion) =>
    isVisible(question, liveAnswers);

  const [isContactInfoModalOpen, setContactInfoModalOpen] = useState(false);
  const closeContactInfoModal = useCallback(
    () => setContactInfoModalOpen(false),
    []
  );

  // Contact info is answered with a modal, so the validator renders no message
  // and the rejected submit has to be caught here.
  const onInvalid = (invalidFields: typeof errors) => {
    if (hasContactInfoError(invalidFields)) setContactInfoModalOpen(true);
  };

  /**
   * An inline question's answer is dropped as soon as its option stops being the
   * selected one, rather than lingering invisibly until submit.
   *
   * Without this, a date typed under "before this date" survived a switch to
   * "asap" and reappeared on switching back — the user was shown a date they
   * had already abandoned, and a cleared field could look refilled.
   *
   * Keyed on the *trigger's* value, not the inline answer's, so clearing the
   * answer cannot re-run this: the dependency is a string, stable across
   * renders, and only the config's own `visible_if` decides what is dropped.
   */
  const inlineTriggerValues = inlineQuestions
    .map(
      ({ question, trigger }) =>
        `${question.key}=${String(liveAnswers[trigger.questionKey] ?? "")}`
    )
    .join("|");

  useEffect(() => {
    inlineQuestions.forEach(({ question }) => {
      if (isInlineActive(question) || !methods.getValues(question.key)) return;

      methods.setValue(question.key, undefined);
      methods.clearErrors(question.key);
    });
  }, [inlineTriggerValues]);

  const onSubmit = async (values: IAnswerMap) => {
    let isPublishTurn = false;

    try {
      // A date typed and then abandoned for "asap" must not reach the draft.
      const kept = { ...values };
      inlineQuestions.forEach(({ question }) => {
        if (!isInlineActive(question)) kept[question.key] = undefined;
      });

      // The address is a draft field, not an answer — see `toDraftFields`.
      const { answers: screenAnswers, fields } = toDraftFields(
        configQuestions,
        kept
      );

      // Rebuilt *before* the save, not after: the answers about to be sent can
      // reveal a screen that was hidden a moment ago — painter's `area` sits
      // behind `surface_type` — and the PUT itself needs to know whether this
      // is the last screen, since that is what asks for the publish.
      // `buildFlowShape` is pure, so running it on the values being saved
      // answers that without waiting for the response.
      const nextScreens = config
        ? buildFlowShape(config, { ...answers, ...kept }, { isLoggedIn, mode })
            .screens
        : screens;
      const next =
        nextScreens[
          nextScreens.findIndex((each) => each.key === currentScreenKey) + 1
        ];

      // A visitor has the auth screen after the last config one, so `!next`
      // alone is never true for them.
      const isLastConfigScreen = !next || next.key === AUTH_SCREEN_KEYS.EMAIL;

      // Editing never publishes — the request already is published.
      isPublishTurn = !next && isLoggedIn && !isEditing;
      if (isPublishTurn) setIsPublishing(true);

      const saved = await submit({
        answers: screenAnswers,
        questions: configQuestions,
        ...fields,
        // Asked for whether or not the visitor is logged in: `validate` runs
        // `ScreenDraft`, and `claim` refuses a draft that was never screened.
        ...(isLastConfigScreen ? { validate: true } : {}),
      });

      // After the PUT, never on the click: HUB-5279 measures answers that
      // reached the draft, and a rejected save throws before this line.
      pushScreenEvent({
        draft,
        screenKey: currentScreenKey,
        isLoggedIn,
        isEditing,
        // `kept`, not `screenAnswers`: `toDraftFields` moves the photo ids out
        // of the answers and into `photo_attachments`, which would leave the
        // photos screen looking permanently unanswered.
        values: kept,
      });

      // A draft started logged out has no owner, so the PUT screened it but
      // could not publish it. `claim` does both, and tracks the publish itself.
      if (isPublishTurn && !saved.user_id) {
        await claim.publishAndFinish();

        return;
      }

      // Only the turn that sent `validate` published. A visitor leaves for the
      // auth page with the draft still unpublished, so counting them here would
      // report a publish that has not happened yet.
      if (isPublishTurn) {
        trackFunnelEvent(
          TRACKING_EVENTS.TASK_PUBLISHED,
          buildTrackingParams({
            draft,
            screenKey: currentScreenKey,
            isLoggedIn,
          })
        );

        if (!currentUser?.hasTasks)
          trackFunnelEvent(TRACKING_EVENTS.FIRST_SERVICE_REQUEST_CREATED);
      }

      // No `next` means the flow shape is out of screens — which is what "the
      // last screen" means here, never the `visibility` key: a later config can
      // put a screen after it. Only a logged-in user gets there: a visitor
      // has the auth screen after the last config one.
      if (!next) {
        // Editing ends where it started: the request's own page, saved and
        // nothing else. A real load for the same reason as `handleSeeMyRequest`
        // below — the request page is Rails-rendered.
        if (isEditing) {
          window.location.assign(
            generateRoute(serviceRequestPathFragment.SERVICE_REQUEST, {
              id: String(taskId),
            })
          );

          return;
        }

        // TODO(RT-4321): a screen reached without a draft cannot have saved
        // anything, so `submit` has already rejected — hence no else branch.
        // Guarded on the id, not the draft: a draft without one must not send
        // the user to `/service_requests/undefined`.
        // The draft's storage is cleared once the user actually leaves via
        // the success modal (`handleSeeMyRequest`), not here: a refresh while
        // the modal is still open must still resume-guard normally, and if it
        // instead hits the published draft's 404 the existing catch above
        // already sends it back to the description screen with a toast.
        if (draft?.id) setPublishedRequestId(String(draft.id));

        return;
      }

      navigate(screenRoute(next.key).fragments, {
        withLocale: true,
        params: screenRoute(next.key).params,
        // Or the theme dies at this hop: `navigate` builds the path from
        // `params` alone (RT-4321).
        queryParams: categoryParams,
      });
    } catch (error) {
      if (handleDraftGone(error)) return;

      toast({
        status: "error",
        description: formatMessage(messages["taskCreation.error.saveFailed"]),
      });
    } finally {
      if (isPublishTurn) setIsPublishing(false);
    }
  };

  // A screen with copy of its own (`area`) uses it; a screen holding one
  // question (`surface_type`) has none, and its `<h1>` is that question's own
  // title — which is why the field there carries no label of its own.
  //
  // The count is of the blocks actually *rendered*, not of the config's
  // questions: `time` declares two, but `desired_date` renders inside an option
  // row, leaving one block — so the option cards get no label of their own and
  // the `<h1>` is the timing question's title, as the design has it.
  const isSingleQuestion = questions.length === 1;
  const titleMessage =
    getScreenTitleMessage(categoryKey, screen?.key ?? "") ??
    (isSingleQuestion
      ? getQuestionTitleMessage(categoryKey, questions[0].key)
      : undefined);
  const subtitleMessage = getScreenSubtitleMessage(
    categoryKey,
    screen?.key ?? ""
  );

  // The photos screen's shared title carries the file cap ("Add your photos (up
  // to 10)"), which is a per-config constraint rather than copy — so the value
  // comes off the screen's own question. Screens with no such constraint pass
  // nothing and interpolate nothing.
  /**
   * The inline question that belongs in a given option's row, if any.
   *
   * It is drawn on its option card unconditionally — the design shows the date
   * chip whether or not "before this date" is selected, and interacting with it
   * is what selects that card (`onActivate`). Whether the value counts is a
   * separate question, answered by `isInlineActive` at validation and submit
   * time.
   */
  const renderInline = (questionKey: string, optionValue: string) => {
    // At most one inline question matches a given option — `find` keeps that
    // single element intact so `SingleSelectQuestionField`'s `isValidElement`
    // check (needed to thread `dateInputRef` via `cloneElement`) succeeds; an
    // array, even of length one, fails that check silently.
    const match = inlineQuestions.find(
      ({ trigger }) =>
        trigger.questionKey === questionKey &&
        trigger.optionValue === optionValue
    );

    if (!match) return null;

    const { question, trigger } = match;
    const Adapter = getQuestionAdapter(question.type);
    if (!Adapter) return null;

    return (
      <Adapter
        key={question.key}
        name={question.key}
        // Required only while its trigger option is the selected one —
        // otherwise an untouched date pill would block a user who picked
        // "asap" from ever leaving the screen.
        question={{
          ...question,
          required: question.required && isInlineActive(question),
        }}
        categoryKey={categoryKey}
        aiValue={prefills[question.key]}
        onEdit={() => dismissPrefill(question.key)}
        onActivate={() =>
          methods.setValue(trigger.questionKey, trigger.optionValue, {
            shouldDirty: true,
          })
        }
      />
    );
  };

  const titleValues = {
    maxFiles: questions.find((each) => each.constraints?.max_files)?.constraints
      ?.max_files,
  };

  return (
    <TaskCreationLayout
      answeredQuestions={answeredQuestions}
      totalQuestions={totalQuestions}
      step={currentStepNumber}
      totalSteps={totalSteps}
      stepName={stepName}
      isLoading={showLoadingState}
    >
      <TaskCreationOutcomeModals
        isPublishing={isPublishing || claim.isPublishing}
        publishedRequestId={publishedRequestId ?? claim.publishedRequestId}
      />
      <ContactInfoModal
        isOpen={isContactInfoModalOpen}
        onClose={closeContactInfoModal}
      />
      <Box paddingY="16px">
        <VStack alignItems="stretch" spacing="16px">
          {hasPrefillForScreen && <PrefillBanner />}
          {titleMessage && (
            <Heading
              as="h1"
              fontFamily="heading"
              fontSize={{ base: "3xl", lg: "5xl" }}
              lineHeight="1"
              color="gray.800"
            >
              <FormattedMessage {...titleMessage} values={titleValues} />
            </Heading>
          )}
          {subtitleMessage && (
            <Text color="gray.600" fontSize="md" lineHeight="150%">
              <FormattedMessage {...subtitleMessage} />
            </Text>
          )}
          <FormProvider {...methods}>
            <form
              onSubmit={methods.handleSubmit(onSubmit, onInvalid)}
              noValidate
            >
              <SimpleGrid
                columns={{ base: 1, lg: questions.length > 1 ? 2 : 1 }}
                spacing="16px"
                alignItems="start"
              >
                {questions.map((question) => {
                  const Adapter = getQuestionAdapter(question.type);

                  // TODO(RT-4321): a type with no adapter is skipped silently,
                  // as the registry promises. Once every type is registered
                  // this branch means a config we cannot render, which is worth
                  // reporting rather than hiding.
                  if (!Adapter) return null;

                  return (
                    <Adapter
                      key={question.key}
                      name={question.key}
                      question={question}
                      categoryKey={categoryKey}
                      aiValue={prefills[question.key]}
                      onEdit={() => dismissPrefill(question.key)}
                      showLabel={!isSingleQuestion}
                      renderOptionTrailing={(optionValue) =>
                        renderInline(question.key, optionValue)
                      }
                      // Only while the option is the selected one: an error
                      // left over from "before this date" must not still show
                      // after switching to "asap", where no date is asked for.
                      renderOptionError={(optionValue) => {
                        const inline = inlineQuestions.find(
                          ({ trigger }) =>
                            trigger.questionKey === question.key &&
                            trigger.optionValue === optionValue
                        );

                        return inline && isInlineActive(inline.question)
                          ? (errors[inline.question.key]?.message as
                              | string
                              | undefined)
                          : undefined;
                      }}
                      onBusyChange={(isBusy) =>
                        setBusyQuestions((current) => ({
                          ...current,
                          [question.key]: isBusy,
                        }))
                      }
                    />
                  );
                })}
              </SimpleGrid>
              <Flex gap="8px" paddingTop="32px" justifyContent="flex-end">
                <Button
                  onClick={handleBack}
                  isDisabled={isSaving}
                  background="gray.300"
                  color="gray.800"
                  height="48px"
                  borderRadius="10px"
                  paddingX="22px"
                  fontSize="md"
                >
                  <FormattedMessage {...messages["taskCreation.cta.back"]} />
                </Button>
                <Button
                  type="submit"
                  isLoading={isSaving}
                  isDisabled={isSaving || isAnyQuestionBusy}
                  colorScheme="seaGreen"
                  height="48px"
                  borderRadius="10px"
                  paddingX="22px"
                  fontSize="md"
                  flex={{ base: "1", lg: "0 0 auto" }}
                >
                  <FormattedMessage
                    {...messages[
                      isEditing
                        ? "taskCreation.cta.saveAndContinue"
                        : "taskCreation.cta.continue"
                    ]}
                  />
                </Button>
              </Flex>
            </form>
          </FormProvider>
        </VStack>
      </Box>
    </TaskCreationLayout>
  );
};
